import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isToday from "date-fns/isToday";
import isTomorrow from "date-fns/isTomorrow";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";

import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import ScrollSyncPanel from "components/ScrollSync/ScrollSyncPanel";
import SortLabel from "components/SortLabel";
import { useRacehorse360Api } from "hooks/api";
import WorkoutsPageState from "interfaces/WorkoutsPageState";
import { SortOrder } from "interfaces/SortOrder";
import { setSortOrder } from "store/actions/workoutsPage";
import { currentTimeZoneString, getCalendar } from "utils/date-utils";
import useStyles from "./styles";

const DAYS_PER_SCREEN: number = 35;

const getNextWorkoutDate = (
  workoutRequests: racehorse360.IWorkoutRequest[]
) => {
  if (workoutRequests.length) {
    const nextDate = parseISO(workoutRequests[0].date);
    const nextWorkouts = workoutRequests.filter(request =>
      isSameDay(parseISO(request.date), nextDate)
    );
    let date;
    if (isToday(nextDate)) {
      date = "Today";
    } else if (isTomorrow(nextDate)) {
      date = "Tomorrow";
    } else {
      date = format(nextDate, "M/d/yyyy");
    }
    return `${date} (${nextWorkouts.length})`;
  }
  return "N/A";
};

const CondensedViewHeader = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { sortOrder, scheduledDatesOnly } = useSelector(
    (state: { workoutsPage: WorkoutsPageState }) => state?.workoutsPage
  );

  const { useListWorkoutRequests } = useRacehorse360Api();

  const { data: workoutRequestsData } = useListWorkoutRequests(
    {
      query: {
        date: {
          range: {
            relative: {
              startDate: { value: 0 },
              endDate: { value: DAYS_PER_SCREEN },
              timezone: currentTimeZoneString()
            }
          }
        },
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      pagingOptions: {
        maxResults: 30,
        includeSummary: true
      },
      getOptions: {
        select: ["id", "horse.id", "date", "status"],
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      enabled: scheduledDatesOnly
    }
  );
  const workoutRequests: racehorse360.IWorkoutRequest[] =
    workoutRequestsData?.workoutRequests || [];

  const months = Object.values(
    getCalendar(startOfToday(), DAYS_PER_SCREEN)
  ).map(month => Object.values(month));

  const handleSort = useCallback(
    (name, newOrder) => {
      dispatch(setSortOrder(newOrder));
    },
    [dispatch]
  );

  return (
    <Box
      className={clsx(classes.root, {
        [classes.scheduledDatesOnlyHeader]: scheduledDatesOnly
      })}
    >
      {scheduledDatesOnly ? (
        <>
          <Typography
            className={clsx(classes.headerLabel, classes.nextWorkout)}
          >
            Next Workout:&nbsp;
            <Typography component={"span"} className={classes.headerValue}>
              {getNextWorkoutDate(workoutRequests)}
            </Typography>
          </Typography>
          <Typography className={clsx(classes.headerLabel, classes.grow)}>
            Total Workouts Scheduled:&nbsp;
            <Typography component={"span"} className={classes.headerValue}>
              {workoutRequestsData?.pagingInfo?.totalResults}
            </Typography>
          </Typography>
          <Box className={classes.sortByContainer}>
            <Typography className={classes.headerLabel}>
              Sort By:&nbsp;
            </Typography>
            <SortLabel
              className={classes.sortLabel}
              columnName={"horseName"}
              sortDirection={sortOrder}
              onSort={handleSort}
            >
              Horse Name
            </SortLabel>
          </Box>
        </>
      ) : (
        <>
          <Typography className={classes.horseNameLabelBlock}>
            <SortLabel
              className={classes.sortLabel}
              columnName={"horseName"}
              sortDirection={sortOrder}
              onSort={handleSort}
            >
              Horse Name
            </SortLabel>
          </Typography>
          <ScrollSyncPanel className={classes.calendar}>
            <Box className={classes.monthContainerWrapper}>
              {months.map(month => (
                <Box key={month[0].date} className={classes.monthContainer}>
                  <Box className={classes.monthTitle}>
                    <Typography className={classes.monthName}>
                      {format(
                        parseISO(month[0].date),
                        month.length > 1 ? "MMMM" : "MMM"
                      )}
                    </Typography>
                    <Typography className={classes.monthDateRange}>
                      {format(parseISO(month[0].date), "d")}
                      &nbsp;-&nbsp;
                      {format(parseISO(month.slice(-1)[0].date), "d")}
                    </Typography>
                  </Box>
                  <Box className={classes.monthDates}>
                    {month.map(date => (
                      <Typography key={date.date} className={classes.monthDate}>
                        {format(parseISO(date.date), "iii")}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}
              <Box className={classes.fake} />
            </Box>
          </ScrollSyncPanel>
        </>
      )}
    </Box>
  );
};

export default React.memo(CondensedViewHeader);
